import { spotWord } from "./place-word";
import maplibregl, { type Map as MlMap, type Marker } from "maplibre-gl";
import { analyze, isSimReady, pathGeometry } from "./sim-client";
import { frameAt, thaiWhen, fromDirection, bearingDeg, type PastStorm } from "./past-storm-core";
import { stormTimeline, type TimedStorm } from "./storms";
import { ensureStormIcon } from "./storm-icon";
import { setTimelineRainDate, rainTimelineDates } from "./province-rain";
import { b4ForDay } from "./dam-rain";
import { summaryState } from "./summary-store";

/**
 * ▶ "การเดินทางของน้ำ" — a forward SIMULATION of water travelling from the
 * northern dams down the Chao Phraya system to the Gulf of Thailand (NOT a replay
 * of recorded time). Points ride the real reach path (one vertex per reach from
 * snap.json); timing comes from the routing engine's velocity law (midpoint of the
 * fast/slow ETA band). Province labels fade in as the water head crosses each
 * province boundary (point-in-polygon against the OCHA tambon layer — positions are
 * computed, never hard-coded). SPEC v0.9 Part B.
 */

const SIM_SEC_PER_REAL_SEC = 6 * 3600; // 1s real = 6h simulated (×speed)
const MAX_POINTS = 150;

// Upstream Chao Phraya reservoirs we release from (coords come from config/dams.json
// via the served dam file; we only name the ones that feed this basin).
const BASIN_DAMS = ["เขื่อนภูมิพล", "เขื่อนสิริกิติ์", "เขื่อนแควน้อยบำรุงแดน", "เขื่อนป่าสักชลสิทธิ์"];

interface Dam {
  name: string; lat: number; lon: number; percent: number | null;
  outflowCms: number | null; kind?: string;
}
interface Src {
  name: string; lon: number; lat: number; color: string;
  outflowCms: number; coords: [number, number][]; cumMid: number[]; cumFast: number[]; cumSlow: number[];
  totalSec: number; etaFast: number; etaSlow: number; particles: boolean;
}
interface ProvStop { province: string; coord: [number, number]; sec: number; fastD: number; slowD: number; special?: string; }
const dayRange = (f: number, s: number): string => `+${f.toFixed(1)}${s > f + 0.05 ? "–" + s.toFixed(1) : ""} วัน`;

const pctColor = (p: number | null): string =>
  p == null ? "#8aa0bf" : p < 60 ? "#22e39b" : p < 80 ? "#ffb020" : p <= 100 ? "#ff8a3c" : "#ff3b5c";

/** Ray-casting point-in-polygon (outer ring only — tambon polygons have no holes). */
function inRing(ring: number[][], lon: number, lat: number): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i]![0]!, yi = ring[i]![1]!, xj = ring[j]![0]!, yj = ring[j]![1]!;
    if ((yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
type TFeat = { bbox?: number[]; properties: { p: string }; geometry: { type: string; coordinates: number[][][] | number[][][][] } };
/** Province name at lon/lat (bbox-prefiltered PIP against the tambon layer). */
function provinceAt(feats: TFeat[], lon: number, lat: number): string | null {
  for (const f of feats) {
    const bb = f.bbox;
    if (bb && (lon < bb[0]! || lon > bb[2]! || lat < bb[1]! || lat > bb[3]!)) continue;
    const g = f.geometry;
    const polys = g.type === "MultiPolygon" ? (g.coordinates as number[][][][]) : [g.coordinates as number[][][]];
    for (const poly of polys) if (poly[0] && inRing(poly[0], lon, lat)) return f.properties.p;
  }
  return null;
}

/** OCHA tambons (CC BY-IGO) — Thai LAND; loaded once, shared by the province stops and the storm "on land" test. */
let tambonCache: Promise<TFeat[]> | null = null;
function tambonFeats(): Promise<TFeat[]> {
  tambonCache ??= fetch("/data/tambons.json").then((r) => r.json()).then((j: { features: TFeat[] }) => j.features).catch(() => []);
  return tambonCache;
}

/** Clutter layers hidden while the animation plays, restored on close. */
const CLUTTER = ["bkk-gauges", "bkk-gauge-labels", "bkk-gauges-dot", "radius-mask", "radius-ring", "gistda-raster"];

export interface FlowAnim {
  open(): Promise<void>; isOpen(): boolean;
  testStorm?(file: string): Promise<boolean>; // test builds only (VITE_TEST_HOOKS=1)
  seek(sec: number): void; span(): { start: number; maxSec: number }; state(): { simSec: number; playing: boolean; jsMsPerFrame: number };
}

export function createFlowAnim(map: MlMap, getUserPoint: () => { lat: number; lon: number } | null): FlowAnim {
  let open = false;
  let raf = 0;
  let simSec = 0;
  let playing = false;
  let speed = 1;
  let maxSec = 1;
  let srcs: Src[] = [];
  let stops: ProvStop[] = [];
  let labelMarkers: { stop: ProvStop; marker: Marker; state: string }[] = [];
  let c13: { lon: number; lat: number; cms: number } | null = null;
  let srcLine = "คำนวณจากอัตราระบายของกรมชลประทาน (SWOC)"; // + report date once loaded
  let userMarker: Marker | null = null;
  let userSec: number | null = null;
  let bar: HTMLElement | null = null;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const prevVis = new Map<string, string>();

  const SRC_ID = "fa-pts", RIP_ID = "fa-rip", STORM_ID = "fa-storm";
  // v0.9.6 Part B — one calendar timeline: t = start + simSec (today = 0)
  let start = Date.now();
  let storms: { name: string; track: PastStorm; enterDir: string | null; test: boolean }[] = [];
  let rainDate: string | null | undefined; // last date painted (undefined = never)
  let rainHeavy: string[] | null = null;
  let rainEnd = 0;                          // ms: end of the last forecast day
  let landFeats: TFeat[] = [];              // OCHA tambons for the storm "on land" test
  let spin = 0, stepAt = 0;
  let testLabel: string | null = null;
  const frameMs: number[] = [];      // e2e render test with a past track (labelled on the bar)

  function setClutter(hidden: boolean): void {
    for (const id of CLUTTER) {
      if (!map.getLayer(id)) continue;
      if (hidden) { prevVis.set(id, (map.getLayoutProperty(id, "visibility") as string) ?? "visible"); map.setLayoutProperty(id, "visibility", "none"); }
      else map.setLayoutProperty(id, "visibility", prevVis.get(id) ?? "visible");
    }
  }

  function posAt(s: Src, sec: number): [number, number] | null {
    if (sec < 0) return null;
    const cum = s.cumMid;
    if (sec >= cum[cum.length - 1]!) return s.coords[s.coords.length - 1]!;
    let i = 0; while (i + 1 < cum.length && cum[i + 1]! <= sec) i++;
    const t0 = cum[i]!, t1 = cum[i + 1] ?? t0, f = t1 > t0 ? (sec - t0) / (t1 - t0) : 0;
    const a = s.coords[i]!, b = s.coords[i + 1] ?? a;
    return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
  }

  // Build particle features: a stream per flowing source, released at sim-time
  // intervals so the line of points marches from dam to gulf. Count ∝ log(discharge),
  // capped globally at MAX_POINTS.
  function buildPoints(): GeoJSON.FeatureCollection {
    const flowing = srcs.filter((s) => s.particles);
    const weights = flowing.map((s) => Math.log10(Math.max(10, s.outflowCms)));
    const wsum = weights.reduce((a, b) => a + b, 0) || 1;
    const feats: GeoJSON.Feature[] = [];
    flowing.forEach((s, si) => {
      const n = Math.max(3, Math.round((weights[si]! / wsum) * MAX_POINTS));
      const spacing = s.totalSec / n;
      for (let j = 0; j < n; j++) {
        const elapsed = simSec - j * spacing;
        if (elapsed < 0 || elapsed > s.totalSec) continue;
        const p = posAt(s, elapsed);
        if (!p) continue;
        feats.push({ type: "Feature", geometry: { type: "Point", coordinates: p }, properties: { color: s.color, lead: j === 0 ? 1 : 0 } });
      }
    });
    return { type: "FeatureCollection", features: feats };
  }

  function rippleFC(t: number): GeoJSON.FeatureCollection {
    const pulse = (Math.sin(t / 500) + 1) / 2; // 0..1
    const feats: GeoJSON.Feature[] = srcs.map((s) => ({
      type: "Feature", geometry: { type: "Point", coordinates: [s.lon, s.lat] },
      properties: { color: s.color, r: 8 + pulse * 10, o: 0.6 - pulse * 0.5 },
    }));
    if (c13) feats.push({ type: "Feature", geometry: { type: "Point", coordinates: [c13.lon, c13.lat] }, properties: { color: "#2f7bff", r: 8 + pulse * 10, o: 0.6 - pulse * 0.5 } });
    return { type: "FeatureCollection", features: feats };
  }

  /** hidden → not reached · full → head is passing now (box shown) · dot → passed
   *  (✓ dot, box on tap). Reversible: derived purely from simSec each frame. */
  function setLabelState(lm: { marker: Marker; state: string }, s: string): void {
    if (lm.state === s) return;
    const el = lm.marker.getElement();
    el.hidden = s === "hidden";
    el.classList.toggle("full", s === "full");
    el.classList.toggle("dot", s === "dot");
    if (s !== "dot") el.classList.remove("open");
    lm.state = s;
  }
  function updateLabels(): void {
    const atGulf = simSec >= maxSec - 1;
    let cur = -1;
    for (let i = 0; i < stops.length; i++) if (stops[i]!.sec <= simSec) cur = i;
    for (let i = 0; i < labelMarkers.length; i++) {
      const isGulf = stops[i]!.province === "อ่าวไทย";
      let s: string;
      if (atGulf) s = isGulf ? "full" : "dot";
      else if (simSec < stops[i]!.sec) s = "hidden"; // not reached
      else if (i === cur) s = "full";               // currently passing
      else s = "dot";                                // passed
      setLabelState(labelMarkers[i]!, s);
    }
  }

  function frame(ts: number): void {
    if (!open) return;
    const t0 = performance.now();
    const pts = map.getSource(SRC_ID) as maplibregl.GeoJSONSource | undefined;
    const rip = map.getSource(RIP_ID) as maplibregl.GeoJSONSource | undefined;
    if (pts) pts.setData(buildPoints());
    if (rip) rip.setData(rippleFC(ts));
    updateLabels();
    if (userMarker && userSec != null) userMarker.getElement().classList.toggle("hit", simSec >= userSec);
    updateTimeline(ts);
    updateBar();
    if (playing && !reduced) { simSec = Math.min(maxSec, simSec + (ts - last) / 1000 * SIM_SEC_PER_REAL_SEC * speed); }
    // reduced motion (B8): still frames, one calendar day per step
    if (playing && reduced && ts - stepAt >= 1400) { stepAt = ts; simSec = Math.min(maxSec, simSec + 86400); }
    last = ts;
    if (playing && simSec >= maxSec) { playing = false; showSummary(); }
    frameMs.push(performance.now() - t0); if (frameMs.length > 120) frameMs.shift(); // B8: our JS cost per frame
    raf = requestAnimationFrame(frame);
  }
  let last = 0;

  /** B1: real calendar, Thai time — "อา. 4 ต.ค. บ่าย" */
  function dayLabel(sec: number): string { return thaiWhen(start + sec * 1000, false); }

  /** B2 storms · B3 rain by day · B4 dam lines · B6 one event line — all from the clock t. */
  function updateTimeline(ts: number): void {
    const t = start + simSec * 1000;
    // B3: provinces by the Thai date of t (only when the date changes)
    const thaiDate = new Date(t + 7 * 3600e3).toISOString().slice(0, 10);
    if (thaiDate !== rainDate) { rainDate = thaiDate; rainHeavy = setTimelineRainDate(thaiDate); }
    // B2: each storm along its real positions; past the last one → fades out
    if (!reduced) spin = (spin - (ts - last) * 0.12) % 360;
    const feats: GeoJSON.Feature[] = [];
    const events: string[] = [];
    for (const st of storms) {
      const f = frameAt(st.track, t);
      const inTh = f.inThailand;
      const label = f.ended ? "สิ้นสุดช่วงพยากรณ์เส้นทาง" : inTh ? `${st.name}` : st.enterDir ? `${st.name} · เข้ามาจากทาง${st.enterDir}` : st.name;
      feats.push({ type: "Feature", properties: { rot: spin, op: f.ended ? 0.25 : 1, label }, geometry: { type: "Point", coordinates: [f.lon, f.lat] } });
      events.push(f.ended ? `พายุ${st.name}: สิ้นสุดช่วงพยากรณ์เส้นทาง` : inTh ? `พายุ${st.name} อยู่บนแผ่นดินไทย` : st.enterDir ? `พายุ${st.name} กำลังเข้ามาจากทาง${st.enterDir}` : `พายุ${st.name} ยังอยู่นอกประเทศไทย`);
    }
    (map.getSource(STORM_ID) as maplibregl.GeoJSONSource | undefined)?.setData(fc(feats));
    // B4: the user's own dam zones, when that day has moderate+ rain there
    const dr = summaryState().damRain;
    if (dr.state === "ok") {
      const i = dr.value.up?.days.findIndex((d) => d.date === thaiDate) ?? dr.value.down?.days.findIndex((d) => d.date === thaiDate) ?? -1;
      if (i >= 0) for (const line of b4ForDay(dr.value, i)) events.push(`${dr.value.dam}: ${line}`);
    }
    // B3 words
    if (t > rainEnd) events.push("สิ้นสุดช่วงพยากรณ์ฝน");
    else if (rainHeavy && rainHeavy.length) events.push(`ฝนหนัก ${rainHeavy.length} จังหวัด: ${rainHeavy.slice(0, 3).join(", ")}${rainHeavy.length > 3 ? "…" : ""}`);
    // B5: where the water head is (model)
    let cur: ProvStop | null = null;
    for (const st of stops) if (st.sec <= simSec) cur = st;
    if (cur) events.push(cur.province === "อ่าวไทย" ? "น้ำจากเขื่อนถึงอ่าวไทย (แบบจำลอง)" : `น้ำจากเขื่อนถึง จ.${cur.province} (แบบจำลอง)`);
    const ev = bar?.querySelector("#fa-event") as HTMLElement | null;
    if (ev) ev.textContent = events.length ? events.slice(0, 2).join(" · ") : "";
    const end = bar?.querySelector("#fa-end") as HTMLElement | null;
    if (end) end.hidden = !(t > rainEnd);
  }

  function extendTimeline(): void {
    const stormEnd = Math.max(0, ...storms.map((st) => Date.parse(st.track.points[st.track.points.length - 1]!.t) - start));
    // + half a day past the last storm position / rain day so "สิ้นสุดช่วงพยากรณ์…" is reachable; ≤ 14 days
    maxSec = Math.min(14 * 86400, Math.max(maxSec, (stormEnd + 12 * 3600e3) / 1000, (rainEnd - start + 6 * 3600e3) / 1000));
  }

  /** Live storms (GDACS via /api/storms) → timed tracks the timeline interpolates (B2). */
  function loadStormsForTimeline(list: TimedStorm[], test = false): void {
    // v0.9.9 C2: "on Thai land" = inside an OCHA tambon (a bounding box would count the Gulf as land);
    // sea areas (อ่าวไทย / ทะเลจีนใต้) have no polygons here → never named
    const inTH = (lon: number, lat: number): boolean => provinceAt(landFeats, lon, lat) != null;
    storms = list.map((s) => {
      const pts = s.points.map((p) => ({ t: new Date(p.t).toISOString(), lat: p.lat, lon: p.lon, windKt: p.windKmh == null ? null : p.windKmh / 1.852, windAgency: null, interpolated: false, inThailand: inTH(p.lon, p.lat) }));
      const k = pts.findIndex((p) => p.inThailand);
      const enterDir = k > 0 ? fromDirection(bearingDeg(pts[k - 1]!, pts[k]!)) : null;
      const track: PastStorm = { sid: s.name, nameEn: s.name, nameTh: null, nameThSource: null, yearBE: 0, entryUtc: pts[Math.max(0, k)]!.t, entryBearingDeg: 0, source: "GDACS", citation: "", citationUrl: "", dataUrl: "", points: pts };
      return { name: s.name, track, enterDir, test };
    });
  }

  function updateBar(): void {
    if (!bar) return;
    (bar.querySelector("#fa-range") as HTMLInputElement).value = String(Math.round((simSec / maxSec) * 1000));
    (bar.querySelector("#fa-day") as HTMLElement).textContent = dayLabel(simSec);
    (bar.querySelector("#fa-play") as HTMLElement).textContent = playing ? "⏸" : "▶";
  }

  function showSummary(): void {
    const spine = srcs.reduce((a, b) => (b.totalSec > a.totalSec ? b : a), srcs[0]!);
    const host = bar?.querySelector("#fa-summary") as HTMLElement | null;
    if (host) host.textContent = `🌊 จากเขื่อนถึงอ่าวไทย ประมาณ ${spine.etaFast.toFixed(0)}–${spine.etaSlow.toFixed(0)} วัน (แบบจำลอง)`;
  }

  function buildBar(): HTMLElement {
    const b = document.createElement("div");
    b.className = "fa-bar";
    b.innerHTML = `
      <div class="fa-disclaimer" id="fa-src">คำนวณจากอัตราระบายของกรมชลประทาน (SWOC)</div>
      <div class="fa-row">
        <button id="fa-play" class="fa-btn" aria-label="เล่น/หยุด">▶</button>
        <input id="fa-range" class="fa-range" type="range" min="0" max="1000" value="0" aria-label="ไทม์ไลน์" />
        <span id="fa-day" class="fa-day">วันนี้</span>
        <span class="fa-speeds">
          <button class="fa-sp on" data-sp="1">1×</button><button class="fa-sp" data-sp="2">2×</button><button class="fa-sp" data-sp="4">4×</button>
        </span>
        <button id="fa-close" class="fa-btn" aria-label="ปิด">✕</button>
      </div>
      <div id="fa-event" class="fa-event" aria-live="polite"></div>
      <div id="fa-end" class="fa-end" hidden>สิ้นสุดช่วงพยากรณ์ฝน</div>
      <div id="fa-test" class="fa-test" hidden></div>
      <div id="fa-summary" class="fa-summary"></div>`;
    b.querySelector("#fa-play")!.addEventListener("click", () => { if (simSec >= maxSec) simSec = 0; playing = !playing; });
    b.querySelector("#fa-close")!.addEventListener("click", () => void close());
    b.querySelector("#fa-range")!.addEventListener("input", (e) => {
      simSec = (Number((e.target as HTMLInputElement).value) / 1000) * maxSec; playing = false;
      if (simSec >= maxSec - 1) showSummary(); else { const h = b.querySelector("#fa-summary"); if (h) h.textContent = ""; }
    });
    b.querySelectorAll<HTMLElement>(".fa-sp").forEach((el) => el.addEventListener("click", () => {
      speed = Number(el.dataset.sp);
      b.querySelectorAll(".fa-sp").forEach((x) => x.classList.toggle("on", x === el));
    }));
    return b;
  }

  async function loadSources(): Promise<void> {
    const data = (await (await fetch("/data/dams-status.json")).json()) as { dams?: Dam[]; chaophraya?: { lat: number; lon: number; outflow_cms: number } | null; swocDate?: string; updatedAt?: string };
    const dams = data.dams ?? [];
    // source + report date on the ▶ bar (no disclaimer wording, v0.9.3)
    const rep = data.swocDate ?? (data.updatedAt ? new Date(data.updatedAt).toLocaleDateString("th-TH", { day: "numeric", month: "short" }) : "");
    srcLine = `คำนวณจากอัตราระบายของกรมชลประทาน (SWOC)${rep ? ` · ${rep}` : ""}`;
    const srcEl = bar?.querySelector("#fa-src"); if (srcEl) srcEl.textContent = srcLine;
    srcs = [];
    for (const name of BASIN_DAMS) {
      const d = dams.find((x) => x.name === name);
      if (!d) continue;
      const r = analyze(d.lon, d.lat, 2.5);
      if (!r || !r.path.length) continue;
      const g = pathGeometry(r.path);
      if (!g || g.coords.length < 2) continue;
      const cumMid = g.cumSecFast.map((f, i) => (f + g.cumSecSlow[i]!) / 2);
      const totalSec = cumMid[cumMid.length - 1]!;
      const outflow = d.outflowCms ?? 0;
      srcs.push({
        name: d.name.replace("เขื่อน", ""), lon: d.lon, lat: d.lat, color: pctColor(d.percent),
        outflowCms: outflow, coords: g.coords, cumMid, cumFast: g.cumSecFast, cumSlow: g.cumSecSlow, totalSec,
        etaFast: r.etaDaysFast, etaSlow: r.etaDaysSlow, particles: outflow > 0,
      });
    }
    maxSec = Math.max(1, ...srcs.map((s) => s.totalSec));
    // B1–B3: today = 0; the timeline also covers the live storms' last position and the rain forecast
    start = Date.now();
    landFeats = await tambonFeats();
    loadStormsForTimeline(stormTimeline());
    const dates = rainTimelineDates();
    rainEnd = dates.length ? Date.parse(`${dates[dates.length - 1]}T00:00:00+07:00`) + 86400e3 : start;
    extendTimeline();
    const cp = data.chaophraya;
    c13 = cp && cp.outflow_cms ? { lon: cp.lon, lat: cp.lat, cms: cp.outflow_cms } : null;
    // province crossings along the longest (spine) path
    const spine = srcs.reduce((a, b) => (b.totalSec > a.totalSec ? b : a), srcs[0]!);
    stops = spine ? await provinceStops(spine) : [];
    // user point ETA (if downstream of the spine): first spine vertex within ~8km
    const up = getUserPoint();
    userSec = up ? nearestSecOnPath(spine, up) : null;
  }

  async function provinceStops(s: Src): Promise<ProvStop[]> {
    const feats = await tambonFeats();
    const out: ProvStop[] = [];
    let lastProv = "";
    for (let i = 0; i < s.coords.length; i += 2) { // sample every other vertex
      const [lon, lat] = s.coords[i]!;
      const prov = provinceAt(feats, lon, lat);
      if (prov && prov !== lastProv) {
        const st: ProvStop = { province: prov, coord: [lon, lat], sec: s.cumMid[i]!, fastD: s.cumFast[i]! / 86400, slowD: s.cumSlow[i]! / 86400 };
        if (prov === "นครสวรรค์") st.special = "ปิง+น่านบรรจบ";
        out.push(st);
        lastProv = prov;
      }
    }
    out.push({ province: "อ่าวไทย", coord: s.coords[s.coords.length - 1]!, sec: s.totalSec, fastD: s.etaFast, slowD: s.etaSlow });
    return out;
  }

  function nearestSecOnPath(s: Src, pt: { lat: number; lon: number }): number | null {
    let best = Infinity, bestSec: number | null = null;
    for (let i = 0; i < s.coords.length; i++) {
      const dx = s.coords[i]![0] - pt.lon, dy = s.coords[i]![1] - pt.lat;
      const d = dx * dx + dy * dy;
      if (d < best) { best = d; bestSec = s.cumMid[i]!; }
    }
    return best < 0.0064 ? bestSec : null; // ~8 km (deg²)
  }

  async function doOpen(): Promise<void> {
    if (open) return;
    if (!isSimReady()) return;
    open = true;
    await loadSources();
    if (!srcs.length) { open = false; return; }

    setClutter(true);
    // fit basin bounds
    const b = new maplibregl.LngLatBounds();
    for (const s of srcs) for (const c of s.coords) b.extend(c as [number, number]);
    // B2: a live storm near Thailand → its positions over Indochina / the near seas are in view too
    for (const st of storms) for (const p of st.track.points) if (p.lon > 95 && p.lon < 112 && p.lat > 4 && p.lat < 23) b.extend([p.lon, p.lat]);
    map.fitBounds(b, { padding: { top: 60, bottom: 180, left: 40, right: 40 }, duration: 800 });

    map.addSource(RIP_ID, { type: "geojson", data: rippleFC(0) });
    map.addLayer({ id: RIP_ID, type: "circle", source: RIP_ID, paint: {
      "circle-radius": ["get", "r"], "circle-color": ["get", "color"], "circle-opacity": 0,
      "circle-stroke-width": 2.5, "circle-stroke-color": ["get", "color"], "circle-stroke-opacity": ["get", "o"] } });
    map.addSource(SRC_ID, { type: "geojson", data: buildPoints() });
    map.addLayer({ id: SRC_ID, type: "circle", source: SRC_ID, paint: {
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 6, ["case", ["==", ["get", "lead"], 1], 7, 4.5], 11, ["case", ["==", ["get", "lead"], 1], 11, 7]],
      "circle-color": ["get", "color"],
      "circle-blur": 0.35,
      "circle-opacity": 0.95,
      "circle-stroke-width": ["case", ["==", ["get", "lead"], 1], 2.5, 0.6], "circle-stroke-color": "#eaffff" } });

    // Province markers: a small dot on the river + a solid offset label box with a
    // short leader. The box shows ONLY while the head is passing that province
    // (frame() toggles full/dot/hidden); passed provinces collapse to a ✓ dot you
    // can tap to re-read. Sides alternate L/R so a box never sits on the river line.
    labelMarkers = stops.map((stop, i) => {
      const el = document.createElement("div");
      el.className = `fa-prov side-${i % 2 ? "l" : "r"}`;
      el.hidden = true;
      const head = `${stop.province === "อ่าวไทย" ? "🌊 " : stop.special ? "◎ " : ""}${stop.province}`;
      const sub = stop.special ? `<span class="fa-prov-sub">${stop.special}</span>` : "";
      el.innerHTML = `<span class="fa-prov-pt"></span><div class="fa-prov-box"><b>${head}</b>${sub}<span class="fa-prov-eta">${dayRange(stop.fastD, stop.slowD)}</span></div>`;
      el.addEventListener("click", () => { if (el.classList.contains("dot")) el.classList.toggle("open"); });
      const marker = new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat(stop.coord).addTo(map);
      return { stop, marker, state: "hidden" };
    });

    // Chao Phraya Dam (C.13): a ripple + a persistent "ระบาย X ลบ.ม./วิ" tag (water
    // passes through here on its way down). SPEC B3.
    if (c13) {
      const el = document.createElement("div"); el.className = "fa-c13";
      el.innerHTML = `<span class="fa-c13-pt"></span><span class="fa-c13-tag">🏞 C.13 ระบาย ${c13.cms.toLocaleString("en-US")} ลบ.ม./วิ</span>`;
      zeroMk.push(new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat([c13.lon, c13.lat]).addTo(map));
    }

    // ripple-only dams (ระบาย 0) get a small "ระบาย 0" tag (no flowing points)
    for (const s of srcs.filter((x) => !x.particles)) {
      const el = document.createElement("div"); el.className = "fa-zero"; el.textContent = `${s.name} · ระบาย 0 (SWOC)`;
      zeroMk.push(new maplibregl.Marker({ element: el, anchor: "top" }).setLngLat([s.lon, s.lat]).addTo(map));
    }

    if (getUserPoint() && userSec != null) {
      const up = getUserPoint()!;
      const el = document.createElement("div"); el.className = "fa-user"; el.innerHTML = `📍<span class="fa-user-eta">ถึง${spotWord()} ~+${(userSec / 86400).toFixed(1)} วัน</span>`;
      userMarker = new maplibregl.Marker({ element: el, anchor: "bottom" }).setLngLat([up.lon, up.lat]).addTo(map);
    }

    bar = buildBar();
    const srcEl = bar.querySelector("#fa-src"); if (srcEl) srcEl.textContent = srcLine; // source + report date
    document.body.appendChild(bar);
    document.body.classList.add("fa-active"); // CSS hides the sheet/FABs for a clean view
    document.addEventListener("visibilitychange", onVis);

    simSec = 0; speed = 1;
    // B2 storm icons (spinning, along the real positions) above everything of the player
    await ensureStormIcon(map);
    map.addSource(STORM_ID, { type: "geojson", data: fc([]) });
    map.addLayer({ id: STORM_ID, type: "symbol", source: STORM_ID, layout: {
      "icon-image": "storm-icon", "icon-size": 0.62, "icon-rotate": ["get", "rot"], "icon-rotation-alignment": "map", "icon-allow-overlap": true, "icon-ignore-placement": true,
      "text-field": ["get", "label"], "text-font": ["Noto Sans Regular"], "text-size": 12, "text-anchor": "top", "text-offset": [0, 1.6], "text-allow-overlap": true, "text-ignore-placement": true },
      paint: { "icon-opacity": ["get", "op"], "text-color": "#e3e0ff", "text-halo-color": "#05080f", "text-halo-width": 1.6, "text-opacity": ["get", "op"] } });
    rainDate = undefined;
    // reduced motion (B8): still frames, one day per step (no continuous movement, no spin)
    playing = true;
    last = performance.now();
    raf = requestAnimationFrame(frame);
  }
  const zeroMk: Marker[] = [];

  function onVis(): void { if (document.hidden) playing = false; }

  async function close(): Promise<void> {
    if (!open) return;
    open = false; playing = false;
    cancelAnimationFrame(raf);
    document.removeEventListener("visibilitychange", onVis);
    for (const id of [SRC_ID, RIP_ID, STORM_ID]) { if (map.getLayer(id)) map.removeLayer(id); if (map.getSource(id)) map.removeSource(id); }
    setTimelineRainDate(null); rainDate = undefined; storms = []; testLabel = null;
    for (const lm of labelMarkers) lm.marker.remove();
    for (const m of zeroMk) m.remove();
    labelMarkers = []; zeroMk.length = 0;
    userMarker?.remove(); userMarker = null;
    bar?.remove(); bar = null;
    document.body.classList.remove("fa-active");
    setClutter(false);
  }

  /** e2e / C6 (TEST BUILDS ONLY — VITE_TEST_HOOKS=1; stripped from the production bundle, owner
   *  5 ต.ค. 2569): render the timeline with a REAL past track (IBTrACS file), shifted so it enters
   *  Thailand 2 days after "today" — always labelled on the bar, never mixed with live storms. */
  const testStorm = import.meta.env.VITE_TEST_HOOKS === "1" ? async (file: string): Promise<boolean> => {
    if (!open) return false;
    let p: PastStorm;
    try { const r = await fetch(`/data/storms-past/${file}`); if (!r.ok) return false; p = (await r.json()) as PastStorm; } catch { return false; }
    const shift = start + 2 * 86400e3 - Date.parse(p.entryUtc);
    loadStormsForTimeline([{ name: p.nameTh ?? p.nameEn, points: p.points.map((q) => ({ t: Date.parse(q.t) + shift, lon: q.lon, lat: q.lat, windKmh: q.windKt == null ? null : q.windKt * 1.852, forecast: false })) }], true);
    testLabel = `ทดสอบการแสดงผลด้วยเส้นทางจริงของพายุ${p.nameTh ?? p.nameEn} ปี ${p.yearBE} (IBTrACS · ข้อมูลย้อนหลัง · เลื่อนเวลาให้เริ่มจากวันนี้)`;
    const el = bar?.querySelector("#fa-test") as HTMLElement | null;
    if (el) { el.textContent = testLabel; el.hidden = false; }
    extendTimeline();
    return true;
  } : undefined;

  return { open: doOpen, isOpen: () => open, ...(testStorm ? { testStorm } : {}), seek: (sec: number) => { simSec = Math.max(0, Math.min(maxSec, sec)); playing = false; }, span: () => ({ start, maxSec }), state: () => ({ simSec, playing, jsMsPerFrame: frameMs.length ? frameMs.reduce((a, b) => a + b, 0) / frameMs.length : 0 }) };
}
const fc = (features: GeoJSON.Feature[]): GeoJSON.FeatureCollection => ({ type: "FeatureCollection", features });
